import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import IArea from '../../../main/interfaces/IArea';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import axiosInstance from '../../../main/initializers/axios';
import {
  ICreateUMBReservationRequest,
  IUMBAvailability,
  IUMBReservation,
  IUpdateUMBReservationRequest,
} from '../../../main/interfaces/IUMBReservation';

interface Props {
  areas: IArea[];
  reservation?: IUMBReservation;
  onClose: () => void;
  onSaved: () => void;
}

const localDate = (date: Date) => {
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const tomorrow = () => {
  const value = new Date();
  value.setDate(value.getDate() + 1);
  return localDate(value);
};

const getInclusiveDays = (startDate: string, endDate: string) => {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
};

const getApiMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ?? error?.response?.data?.Message ?? error?.response?.data?.error ?? fallback;

const CreateUmbReservationDialog: React.FC<Props> = ({ areas, reservation, onClose, onSaved }) => {
  const { t, i18n } = useTranslation();
  const isEdit = Boolean(reservation);
  const initialDate = useMemo(tomorrow, []);
  const [form, setForm] = useState({
    customerName: reservation?.customerName ?? '',
    customerPhone: reservation?.customerPhone ?? '',
    customerEmail: reservation?.customerEmail ?? '',
    startDate: reservation?.startDate?.slice(0, 10) ?? initialDate,
    endDate: reservation?.endDate?.slice(0, 10) ?? initialDate,
    depositAmount: reservation?.depositAmount == null ? '' : String(reservation.depositAmount),
    notes: reservation?.notes ?? '',
    areaId: 0,
  });
  const [availability, setAvailability] = useState<IUMBAvailability[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>(reservation?.items.map((item) => item.umbId) ?? []);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [umbrellaFilter, setUmbrellaFilter] = useState('');
  const [rowFilter, setRowFilter] = useState('');

  const loadAvailability = useCallback(async () => {
    if (!form.startDate || !form.endDate || form.endDate < form.startDate) {
      setAvailability([]);
      return;
    }

    setLoadingAvailability(true);
    setError('');
    try {
      const response = await axiosInstance.post<IUMBAvailability[]>(ApiConstants.umbReservation.availability, {
        startDate: form.startDate,
        endDate: form.endDate,
        areaId: form.areaId || null,
        excludeReservationId: reservation?.id ?? null,
      });
      setAvailability(response.data ?? []);
      setSelectedIds((current) =>
        current.filter((id) => response.data.some((item) => item.umbId === id && item.isAvailableForEntireRange)),
      );
    } catch (requestError) {
      setError(getApiMessage(requestError, t('umbReservations.errors.availability')));
    } finally {
      setLoadingAvailability(false);
    }
  }, [form.areaId, form.endDate, form.startDate, reservation?.id, t]);

  useEffect(() => {
    const timer = window.setTimeout(loadAvailability, 250);
    return () => window.clearTimeout(timer);
  }, [loadAvailability]);

  const selected = useMemo(
    () => availability.filter((item) => selectedIds.includes(item.umbId)),
    [availability, selectedIds],
  );
  const filteredAvailability = useMemo(() => {
    const umbrellaQuery = umbrellaFilter.trim().toLowerCase();
    const rowQuery = rowFilter.trim().toLowerCase();

    return availability.filter((item) => {
      const umbrellaText = `${item.name ?? ''} ${item.code ?? ''}`.toLowerCase();
      const rowText = `${item.rowName ?? ''} ${item.rowCode ?? ''}`.toLowerCase();
      return (!umbrellaQuery || umbrellaText.includes(umbrellaQuery))
        && (!rowQuery || rowText.includes(rowQuery));
    });
  }, [availability, rowFilter, umbrellaFilter]);
  const dayCount = getInclusiveDays(form.startDate, form.endDate);
  const estimatedTotal = selected.reduce((sum, item) => sum + Number(item.firstPrice || 0) * dayCount, 0);

  const update = (key: keyof typeof form, value: string | number) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const toggleUmbrella = (item: IUMBAvailability) => {
    if (!item.isAvailableForEntireRange) return;
    setSelectedIds((current) =>
      current.includes(item.umbId) ? current.filter((id) => id !== item.umbId) : [...current, item.umbId],
    );
  };

  const validate = () => {
    if (!form.customerName.trim()) return t('umbReservations.validation.customerName');
    if (!form.startDate || !form.endDate || form.endDate < form.startDate) return t('umbReservations.validation.dateRange');
    if (Number(form.depositAmount || 0) < 0) return t('umbReservations.validation.deposit');
    if (!selected.length) return t('umbReservations.validation.umbrellas');
    return '';
  };

  const submit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const baseRequest: ICreateUMBReservationRequest = {
      customerName: form.customerName.trim(),
      customerPhone: form.customerPhone.trim() || null,
      customerEmail: form.customerEmail.trim() || null,
      startDate: form.startDate,
      endDate: form.endDate,
      notes: form.notes.trim() || null,
      depositAmount: form.depositAmount === '' ? null : Number(form.depositAmount),
      items: selected.map((item) => ({
        umbId: item.umbId,
        lockedDailyPrice: Number(
          reservation?.items.find((existing) => existing.umbId === item.umbId)?.lockedDailyPrice
            ?? item.firstPrice
            ?? 0,
        ),
      })),
    };
    const request: ICreateUMBReservationRequest | IUpdateUMBReservationRequest = isEdit
      ? { ...baseRequest, reservationId: reservation!.id }
      : baseRequest;

    setSaving(true);
    setError('');
    try {
      await axiosInstance.post(
        isEdit ? ApiConstants.umbReservation.update : ApiConstants.umbReservation.create,
        request,
      );
      toast.success(t(isEdit ? 'umbReservations.messages.updated' : 'umbReservations.messages.created'));
      onSaved();
    } catch (requestError: any) {
      const message = getApiMessage(
        requestError,
        t(isEdit ? 'umbReservations.errors.update' : 'umbReservations.errors.create'),
      );
      setError(message);
      if (requestError?.response?.status === 409) await loadAvailability();
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="umbr-modal-overlay" role="dialog" aria-modal="true">
      <div className="umbr-modal umbr-create-modal">
        <div className="umbr-modal-header">
          <div>
            <h2>{t(isEdit ? 'umbReservations.editReservation' : 'umbReservations.newReservation')}</h2>
            {/* <p>{t(isEdit ? 'umbReservations.editSubtitle' : 'umbReservations.createSubtitle')}</p> */}
          </div>
          <button type="button" className="umbr-close" onClick={onClose} aria-label={t('generic.close')}>
            x
          </button>
        </div>

        <div className="umbr-modal-body">
          {error && <div className="umbr-alert is-error">{error}</div>}
          <div className="umbr-form-grid">
            <Field label={t('umbReservations.customerName')} required className="umbr-field--customer">
              <input value={form.customerName} onChange={(event) => update('customerName', event.target.value)} />
            </Field>
            <Field label={t('umbReservations.customerPhone')}>
              <input value={form.customerPhone} onChange={(event) => update('customerPhone', event.target.value)} />
            </Field>
            {/* <Field label={t('umbReservations.customerEmail')}>
              <input type="email" value={form.customerEmail} onChange={(event) => update('customerEmail', event.target.value)} />
            </Field> */}
            <Field label={t('umbReservations.deposit')}>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.depositAmount}
                onChange={(event) => update('depositAmount', event.target.value)}
              />
            </Field>
            <Field label={t('generic.startDate')} required>
              <input type="date" value={form.startDate} onChange={(event) => update('startDate', event.target.value)} />
            </Field>
            <Field label={t('generic.endDate')} required>
              <input type="date" min={form.startDate} value={form.endDate} onChange={(event) => update('endDate', event.target.value)} />
            </Field>
            <Field label={t('area.area')} className="umbr-field--area">
              <select value={form.areaId} onChange={(event) => update('areaId', Number(event.target.value))}>
                <option value={0}>{t('umbReservations.allAreas')}</option>
                {areas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="umbr-selection-summary">
              <span>{t('umbReservations.selectedCount', { count: selected.length })}</span>
              <strong>{t('umbReservations.estimatedTotal', { total: estimatedTotal.toFixed(2) })}</strong>
              <small>{t('umbReservations.inclusiveDays', { count: dayCount })}</small>
            </div>
          </div>

          <Field label={t('umbReservations.notes')}>
            <textarea value={form.notes} onChange={(event) => update('notes', event.target.value)} />
          </Field>

          <div className="umbr-availability-heading">
            <div>
              <h3>{t('umbReservations.chooseUmbrellas')}</h3>
              {/* <p>{t('umbReservations.availabilityHint')}</p> */}
            </div>
            <div className="umbr-availability-filters">
              <Field label={t('umbReservations.umbrellaNameCodeFilter')}>
                <input
                  type="search"
                  value={umbrellaFilter}
                  onChange={(event) => setUmbrellaFilter(event.target.value)}
                  placeholder={t('umbReservations.umbrellaNameCodePlaceholder')}
                />
              </Field>
              <Field label={t('umbReservations.rowFilter')}>
                <input
                  type="search"
                  value={rowFilter}
                  onChange={(event) => setRowFilter(event.target.value)}
                  placeholder={t('umbReservations.rowFilterPlaceholder')}
                />
              </Field>
            </div>
            {loadingAvailability && <span>{t('umbReservations.loading')}</span>}
          </div>

          {!loadingAvailability && availability.length === 0 ? (
            <div className="umbr-empty">{t('umbReservations.noUmbrellas')}</div>
          ) : !loadingAvailability && filteredAvailability.length === 0 ? (
            <div className="umbr-empty">{t('umbReservations.noFilteredUmbrellas')}</div>
          ) : (
            <div className="umbr-umbrella-grid">
              {filteredAvailability.map((item) => {
                const isSelected = selectedIds.includes(item.umbId);
                const conflicts = item.conflictingDates
                  .map((date) => new Date(date).toLocaleDateString(i18n.language))
                  .join(', ');
                return (
                  <button
                    type="button"
                    key={item.umbId}
                    className={`umbr-umbrella-card ${item.isAvailableForEntireRange ? 'is-available' : 'is-unavailable'} ${
                      isSelected ? 'is-selected' : ''
                    }`}
                    disabled={!item.isAvailableForEntireRange}
                    onClick={() => toggleUmbrella(item)}
                    title={conflicts ? `${t('umbReservations.conflictingDates')}: ${conflicts}` : undefined}
                  >
                    {isSelected && <CheckCircleIcon className="umbr-selected-check" />}
                    <BeachAccessIcon />
                    <strong>{item.name}</strong>
                    <span>{item.code}</span>
                    <small>{item.areaName || t('umbReservations.noArea')}</small>
                    <b>{Number(item.firstPrice || 0).toFixed(2)}</b>
                    {!item.isAvailableForEntireRange && (
                      <em>{t('umbReservations.conflictsCount', { count: item.conflictingDates.length })}</em>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="umbr-modal-footer">

           <button type="button" className="ui-control ui-btn ui-btn--primary" onClick={submit} disabled={saving}>
            {saving
              ? t('umbReservations.saving')
              : t(isEdit ? 'umbReservations.updateReservation' : 'umbReservations.create')}
          </button>
          
          <button type="button" className="ui-control ui-btn ui-btn--secondary" onClick={onClose}>
            {t('generic.close')}
          </button>
         
        </div>
      </div>
    </div>,
    document.body,
  );
};

const Field: React.FC<{ label: string; required?: boolean; className?: string; children: React.ReactNode }> = ({
  label,
  required,
  className = '',
  children,
}) => (
  <label className={`umbr-field ${className}`.trim()}>
    <span>
      {label}
      {required ? ' *' : ''}
    </span>
    {children}
  </label>
);

export default CreateUmbReservationDialog;
